// [PURPOSE]: Sub-plan 1C item 3 — pure helpers for the unidentified-loot
//            flag gate.
//
// [WHY]: Sub-plan 1A wired the `isIdentified` field into CustomItemCreator
//        (default true; emitted to save ONLY when explicitly false), but
//        no consumer reads it. This commit lands the v1 minimum gate:
//          • `isItemIdentified(item)` — boolean property check
//          • `getItemDisplayName(item)` — masked name for the inventory grid
//          • `getItemDisplayDescription(item)` — masked description
//
//        Consumer wiring: InventoryItem constructor (App.jsx) propagates
//        the flag, ContextMenuComponent surfaces an "Identify" action,
//        InventoryContext adds an `identifyItem` mutator, GridComponent
//        uses the masked-name helper for the cell label. Full DM-loot
//        autoflag (drops items unidentified by default) + Identify-spell
//        wiring deferred to v1.x.
//
// [SCOPE — v1 minimum]: Property-based detection only. The gate fires when
//        `item.isIdentified === false` (literal). Any other shape — true,
//        undefined, 1, 'true', etc. — is treated as identified. This mirrors
//        the CustomItemCreator convention (default-true / save-only-on-false)
//        and keeps legacy items from getting accidentally masked.
//
// [RULE 11]: Property-based detection — no string-domain matching on item names.
//
// [SPEC]: Master-queue Sub-plan 1C item 3. The author-side flag lives in
//         CustomItemCreator.js:188 (blankItem) + L575-577 (save handler).

// Mapping from item.category → friendly category label for the masked name.
// Add entries as new categories surface. Falls back to "Item" for unknowns.
const CATEGORY_LABELS = {
  weapons: 'Weapon',
  armor: 'Armor',
  magical_items: 'Magic Item',
  ammunition: 'Ammunition',
  items: 'Item',
  consumables: 'Consumable',
  potions: 'Potion',
  scrolls: 'Scroll',
  tools: 'Tool',
  treasure: 'Treasure',
};

/**
 * Returns true if the item is identified (or the flag is absent — default).
 * Returns false ONLY when `item.isIdentified === false` literally.
 *
 * @param {object|null|undefined} item
 * @returns {boolean}
 */
export function isItemIdentified(item) {
  if (!item || typeof item !== 'object') return true;
  return item.isIdentified !== false;
}

/**
 * Returns the display name for an inventory cell / list entry. Identified
 * items return their real `item.name`. Unidentified items return a generic
 * "Unidentified <Category>" label that hides the magical specifics.
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function getItemDisplayName(item) {
  if (!item || typeof item !== 'object') return '';
  if (isItemIdentified(item)) return item.name || '';

  const label = (item.category && CATEGORY_LABELS[item.category]) || 'Item';
  return `Unidentified ${label}`;
}

/**
 * Returns the display description. Identified items return the real
 * description. Unidentified items return a generic prompt — the real
 * description does NOT leak (avoids partial reveals via tooltip content).
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
export function getItemDisplayDescription(item) {
  if (!item || typeof item !== 'object') return '';
  if (isItemIdentified(item)) return item.description || '';

  const label = (item.category && CATEGORY_LABELS[item.category]) || 'Item';
  return `An unidentified ${label.toLowerCase()}. Identify it (or attune through use) to learn its properties.`;
}
